import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ActionLink } from "@wine2trace/ui/FormComponents";
import { addDays, format } from "date-fns";
import {
  selectActiveWinery,
  selectAnalysis,
  selectConfigs,
  selectLots,
  selectTanks,
  selectUser,
} from "../../context/selectors";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useLabels from "../../hooks/useLabels";
import usePermissions from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../routeAccess";
import { getItem } from "../../services/LocalStorage";
import { getCompletedOperationsWithPaging } from "../../services/utils";
import { describeOperationTargets } from "./nextOperationsRows";
import { pickRecentAnalyses } from "./recentAnalysis";

const DEFAULT_RECENT_DAYS = 14;
const FETCH_LIMIT = 100;

// "Ultime analisi": a compact list that replaces the carousel of tiles on the Home. The full
// tile grid stays on the lot and tank detail pages.
const HomeRecentAnalysis = () => {
  const [getLabel] = useLabels();
  const axiosPrivate = useAxiosPrivate();
  const activeWinery = useSelector(selectActiveWinery);
  const user = useSelector(selectUser);
  const configs = useSelector(selectConfigs);
  const analysis = useSelector(selectAnalysis);
  const tanks = useSelector(selectTanks);
  const lots = useSelector(selectLots);
  const { can } = usePermissions();
  const canSeeOperations = can(...PERMISSIONS.operationsRead);
  const [operations, setOperations] = useState([]);

  useEffect(() => {
    if (!canSeeOperations) return undefined;
    let isMounted = true;
    (async () => {
      const recentDays = parseInt(
        getItem("analysisRecentlyCompletedDays") || user?.settings?.analysis_recently_completed_days || DEFAULT_RECENT_DAYS
      );
      try {
        const completed = await getCompletedOperationsWithPaging(
          axiosPrivate,
          addDays(new Date(), -recentDays).toISOString(),
          addDays(new Date(), 1),
          FETCH_LIMIT
        );
        if (isMounted) setOperations(pickRecentAnalyses(completed));
      } catch (e) {
        if (isMounted) setOperations([]);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [activeWinery?.id, canSeeOperations]);

  const rows = useMemo(
    () =>
      operations.map((operation) => ({
        operation,
        protocol: analysis?.find((exam) => exam.protocols_id == operation.protocol_id)?.protocols_name,
        targets: describeOperationTargets(operation, { tanks, lots }),
      })),
    [operations, analysis, tanks, lots]
  );

  if (!canSeeOperations) return null;

  return (
    <section className="home_panel" aria-label={getLabel("lastAnalysis")}>
      <h4 className="home_panel_title">{getLabel("lastAnalysis")}</h4>
      {rows.length === 0 ? (
        <p className="home_panel_empty">{getLabel("noAnalysisCompleted")}</p>
      ) : (
        <ul className="home_analysis">
          {rows.map(({ operation, protocol, targets }) => (
            <li key={operation.id} className="home_analysis_item">
              <ActionLink to={`/programming/analysis/${operation.id}`} variant="strong">
                {getLabel("operationTypeANALYSIS")} #{operation.id}
                {protocol ? ` [${protocol}]` : ""}
              </ActionLink>
              <span className="home_analysis_targets">
                {[...targets.lots, ...targets.tanks].map((name) => (
                  <span key={name} className="home_analysis_target">
                    {name}
                  </span>
                ))}
              </span>
              <span className="home_analysis_date">{format(new Date(operation.completion_date), configs.dateFormat)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default HomeRecentAnalysis;
